/* eslint-disable complexity */
import React, { FC, memo, MouseEvent, useContext } from 'react';
import { useParams } from 'react-router-dom';

import Icon from 'carbon-react/lib/components/icon';
import Tooltip from 'carbon-react/lib/components/tooltip';

import { matchOnboardingRoute } from '@futrli/shared-services';
import { FormattedMessage } from '@futrli/localisation';

import { AccountRow, CalculationRow, TotalRow, GroupRow, OrganisationRow } from '../types';
import { BUDGET_ONBOARDING_STEP } from '../../../../../forecast/onboarding/const';
import { DeferVatButton } from './components/defer-vat-button/defer-vat-button';
import { ROW_KIND } from '../constants';
import { trackEvent } from '../../../../../core/tracker';
import { TRACKING_EVENTS } from '../../../../../core/constants';
import { usePredictionsInfoContext } from '../../../context/predictions-info-context';
import { UserGridSettingsContext } from '../../user-grid-settings/user-grid-settings-context';

import gridStyles from '../../../../../ui/data-grid/components/grid.scss';
import styles from './header-row-account-title.scss';

interface Props {
  row: TotalRow | AccountRow | CalculationRow | GroupRow | OrganisationRow;
  isExpandedRow: boolean;
  showDeferVat?: boolean;
}

interface AccountTitleProps {
  isJoinedRow: boolean;
  name: string;
}

const AccountTitle: FC<AccountTitleProps> = ({ name, isJoinedRow }) => (
  <div className={gridStyles.title} data-testid="account-name">
    {name}
    {isJoinedRow && (
      <div className={gridStyles.joinedRowTitle}>
        <FormattedMessage id="predict.as_percent_of_income" defaultMessage="As a % of income" />
      </div>
    )}
  </div>
);

export const HeaderRowAccountTitle: FC<Props> = memo(({ row, isExpandedRow, showDeferVat }) => {
  const { onboardingStep: tempOnboardingStep } = useParams();
  const onboardingStep = tempOnboardingStep || '';

  const {
    childCount,
    kind,
    account: { index, name }
  } = row;

  const { toggleExpandedRows } = useContext(UserGridSettingsContext);
  const { isReadOnly } = usePredictionsInfoContext();

  const handleToggleExpand = (event: MouseEvent<HTMLElement>) => {
    const current = event.currentTarget;

    if (!isExpandedRow) {
      trackEvent(TRACKING_EVENTS.EXPANDED_ACCOUNT);
      trackEvent(TRACKING_EVENTS.EXPANDED_ACCOUNT_LINE);
    }

    toggleExpandedRows(current.id);
  };

  const hasChild = childCount >= 1;
  const expandedRow = isExpandedRow && hasChild;
  const isJoinedRow = kind === ROW_KIND.calculation && (row as CalculationRow).joinedRow !== undefined;

  const hideExpandingRows =
    matchOnboardingRoute(location.pathname) && onboardingStep === BUDGET_ONBOARDING_STEP.SELECT_TYPE;

  if (childCount < 1 || (row.kind === ROW_KIND.total && row.forceRowSize === 'medium')) {
    return <AccountTitle name={name} isJoinedRow={isJoinedRow} />;
  }

  return (
    <div
      style={
        showDeferVat ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' } : {}
      }
    >
      {hideExpandingRows || !hasChild ? (
        <div className={styles.expandableTitle} style={{ paddingLeft: 20 }}>
          <AccountTitle name={name} isJoinedRow={isJoinedRow} />
        </div>
      ) : (
        <Tooltip
          message={
            expandedRow ? (
              <FormattedMessage id="common.collapse_row" defaultMessage="Collapse row" />
            ) : (
              <FormattedMessage id="common.expand_row" defaultMessage="Expand row" />
            )
          }
          position="top"
        >
          <a
            className={styles.expandableTitle}
            onClick={handleToggleExpand}
            id={index}
            data-testid={`expand-account-row-${index}`}
          >
            {expandedRow ? (
              <Icon type="caret_down" data-testid="collapse-arrow-down" />
            ) : (
              <Icon type="caret_right" data-testid="expand-arrow-right" />
            )}
            <AccountTitle name={name} isJoinedRow={isJoinedRow} />
          </a>
        </Tooltip>
      )}
      {showDeferVat && !isReadOnly && <DeferVatButton />}
    </div>
  );
});
